import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DiscoverItem } from "../../types";

import { Icon } from "@majourneys/icons";
interface TripStopMenuProps {
  readonly item: DiscoverItem;
  readonly canMoveEarlier: boolean;
  readonly canMoveLater: boolean;
  readonly hasNote: boolean;
  readonly onView: () => void;
  readonly onEditNote: () => void;
  readonly onMoveEarlier: () => void;
  readonly onMoveLater: () => void;
  readonly onRemove: () => void;
}

/**
 * Kebab (⋯) menu attached to each itinerary stop. Inspired by the
 * mindtrip per-item popover with View / Move to prev day / Move to
 * next day / Remove. Closes on outside click or Escape.
 */
export function TripStopMenu({
  item,
  canMoveEarlier,
  canMoveLater,
  hasNote,
  onView,
  onEditNote,
  onMoveEarlier,
  onMoveLater,
  onRemove,
}: TripStopMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="mj-trip-stop-menu" ref={wrapRef}>
      <button
        type="button"
        className="mj-trip-stop-menu__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("trip.stop_actions", { title: item.title })}
      >
        <Icon name="ri-more-2-fill" aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" className="mj-trip-stop-menu__popover">
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-menu__item"
            onClick={() => {
              close();
              onView();
            }}
          >
            <Icon name="ri-external-link-line" aria-hidden="true" />
            {t("trip.view_item", { title: item.title })}
          </button>
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-menu__item"
            onClick={() => {
              close();
              onEditNote();
            }}
          >
            <Icon name={hasNote ? "ri-sticky-note-fill" : "ri-sticky-note-line"} aria-hidden="true" />
            {hasNote ? t("trip.edit_note") : t("trip.add_note")}
          </button>
          <span className="mj-trip-stop-menu__divider" aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-menu__item"
            disabled={!canMoveEarlier}
            onClick={() => {
              close();
              onMoveEarlier();
            }}
          >
            <Icon name="ri-arrow-up-s-line" aria-hidden="true" />
            {t("trip.move_previous_day")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-menu__item"
            disabled={!canMoveLater}
            onClick={() => {
              close();
              onMoveLater();
            }}
          >
            <Icon name="ri-arrow-down-s-line" aria-hidden="true" />
            {t("trip.move_next_day")}
          </button>
          <span className="mj-trip-stop-menu__divider" aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-menu__item mj-trip-stop-menu__item--danger"
            onClick={() => {
              close();
              onRemove();
            }}
          >
            <Icon name="ri-delete-bin-line" aria-hidden="true" />
            {t("trip.remove")}
          </button>
        </div>
      )}
    </div>
  );
}
